function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compact } from 'lodash-es';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { Button, Callout, ConfirmationModal, FunctionalLoader, GridItem, Spinner, MenuItem, SplitButton, TextField, Typography, useStateInRef } from 'seagull';
import { ArchiveLiner24, Checkmark, CloseLiner24, CopyLiner24, TrashLine } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { validateReviewers } from "../../business-logic/draftLogic";
import { templateNameValidation } from "../../business-logic/validators";
import SignatoryCheckbox from "./SignatoryCheckbox/SignatoryCheckbox";
import { defaultAgreementTitle, routes } from "../../constants";
import { useEventReporter, useOverlayContext, useIoContext, useStore, useStoreActions } from "../../contexts";
import { useContactsMap } from "../../hooks/reactQuery/derived/useContactsMap";
import { useArchiveDraft } from "../../hooks/reactQuery/mutations/useArchiveDraft";
import { useCopyProposalLink } from "../../hooks/useCopyProposalLink";
import { useCreateRelationshipFromDraft } from "../../hooks/useCreateRelationshipFromDraft";
import { useOnMount } from "../../hooks/useOnMount";
import { useScheduledProposal } from "../../hooks/useScheduledProposal";
import { useSetWorksAtOptimisticUpdate } from "../../hooks/useSetWorksAtOptimisticUpdate";
import { useSignal } from "../../hooks/useSignal";
import { Intent } from "../../infra/access-control";
import { ErrorResponse, ServerError } from "../../infra/ErrorResponse";
import { makeNetTerms } from "../../infra/translator";
import { handleRequest, sendErrReport, wait } from "../../infra/utils";
import { helpArticles } from "../../utils/helpArticles";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import { ClientPreview } from "../ClientPreview/ClientPreview";
import ConfirmExternallySignModal from "../ConfirmExternallySignModal/ConfirmExternallySignModal";
import ExternalLinkButton from "../ExternalLinkButton";
import { RelationshipEffectiveDate } from "../RelationshipEffectiveDate/RelationshipEffectiveDate";
import AddAdditionalRecipients from "./AddAdditionalRecipients/AddAdditionalRecipients";
import { AgreementName } from "./AgreementName";
import styles from "./AgreementStudio.module.scss";
import { contactHasAgreement } from "./AgreementStudio.utils";
import { hasBilledImmediatelyService, makeDraft } from "./AgreementStudioProposalMode.utils";
import AgreementStudioWithLegalTermsModal, { SubmitType } from "./AgreementStudioWithLegalTermsModal";
import ContactDetails from "./ContactDetails/ContactDetails";
import { PostAgreementCreation } from "./PostAgreementCreation";
import ReadyToSendModal from "./ReadyToSendModal/ReadyToSendModal";
import ScheduleProposal from "./ScheduleProposal/ScheduleProposal";
import { CopyLinksModal } from "./SilentProposal/CopyLinksModal";
import { GenerateLinkModal } from "./SilentProposal/GenerateLinkModal";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const createRelationshipLoader = 'createRelationship';
var DraftStatus = /*#__PURE__*/function (DraftStatus) {
  DraftStatus["Loading"] = "loading";
  DraftStatus["Saved"] = "saved";
  DraftStatus["Error"] = "error";
  DraftStatus["None"] = "none";
  return DraftStatus;
}(DraftStatus || {});
function SaveAsTemplateModal(_ref) {
  let {
    draftId,
    dismissModal
  } = _ref;
  const {
    api
  } = useStoreActions();
  const {
    showSuccessToast,
    showErrorToast
  } = useOverlayContext();
  const {
    control,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      name: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const closeModal = () => dismissModal(false);
  async function submit(_ref2) {
    let {
      name
    } = _ref2;
    setLoading(true);
    const result = await handleRequest(api.proposalTemplates.createFromDraft(draftId, name));
    if (result.success) {
      showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
      dismissModal(true);
    } else {
      if (new ErrorResponse(result.error).hasServerError(ServerError.NameAlreadyExists)) {
        showErrorToast('A template with this name already exists', 'proposal-template-name-already-used');
      } else {
        showErrorToast('There was an error saving this template', 'proposal-template-not-saved-toast');
      }
    }
    setLoading(false);
  }
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    title: "Save as template",
    "data-testid": "save-as-proposal-template-modal",
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Controller, {
        name: "name",
        control: control,
        rules: templateNameValidation,
        render: p => /*#__PURE__*/_jsx(TextField, _objectSpread({
          autoFocus: true,
          testid: "proposal-template-name",
          label: "Template name"
        }, connectControllerToTextField(p)))
      }), /*#__PURE__*/_jsx(Callout, {
        style: {
          marginTop: '32px'
        },
        children: "Your template can be managed and used through the \"Proposal Templates\" page."
      })]
    }),
    primaryButtonLabel: "Save",
    primaryButtonLoading: loading,
    onPrimaryButtonClick: handleSubmit(submit),
    secondaryButtonLabel: "Cancel",
    onSecondaryButtonClick: closeModal,
    onCloseButtonClick: closeModal
  });
}
function getSavingStatusMP(draftStatus) {
  const testid = "save-indicator-".concat(draftStatus);
  switch (draftStatus) {
    case DraftStatus.None:
      return null;
    case DraftStatus.Loading:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Spinner, {
          size: 24
        }), /*#__PURE__*/_jsx(Typography, {
          role: "alert",
          as: "p",
          "data-testid": testid,
          children: "Saving draft"
        })]
      });
    case DraftStatus.Saved:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.checkmarkContainer,
          children: /*#__PURE__*/_jsx(Checkmark, {})
        }), /*#__PURE__*/_jsx(Typography, {
          "data-testid": testid,
          children: "Draft saved"
        })]
      });
    case DraftStatus.Error:
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.savingError,
          children: /*#__PURE__*/_jsx(CloseLiner24, {})
        }), /*#__PURE__*/_jsx(Typography, {
          "data-testid": testid,
          children: "Draft save error"
        })]
      });
  }
}
export const AgreementStudioProposalModeTestIds = pathgen('AgreementStudioProposalModeTestIds');
export function AgreementStudioProposalMode(props) {
  var _ref3, _draft$isSample;
  const {
    viewProposalMode
  } = props;
  const {
    archive
  } = useArchiveDraft({
    withPageLoader: true,
    onSuccess: () => history.push(routes.draftsDashboard),
    biCode: 18801
  });
  const isViewProposal = !!viewProposalMode;
  const draft = viewProposalMode ? viewProposalMode.proposalAsDraft : props.draft;
  const {
    reportToAppcues,
    reportToGtm,
    reportToHubspot,
    reportToHeap
  } = useEventReporter();
  const history = useHistory();
  const {
    prepareFormObject,
    reportProductsFormSubmission,
    reportButtonClick
  } = useEventReporter();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const legalTermsDocuments = useStore(state => state.legalTerms.data);
  const agreements = useStore(state => state.agreements.data);
  const proposals = useStore(state => state.proposals.data);
  const createRelationshipFromDraft = useCreateRelationshipFromDraft();
  const updateWorksAt = useSetWorksAtOptimisticUpdate();
  const scrollToTopRef = useRef(null);
  const [newContactValid, setNewContactValid] = useState(true);
  const [titleValid, setTitleValid] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) != null);
  const [clientContactId, setClientContactId] = useState(draft === null || draft === void 0 ? void 0 : draft.clientContactId());
  const [showAgreementTitleWarning, setShowAgreementTitleWarning] = useState(false);
  const locationState = useLocation().state;
  const [isSampleRelationship, setIsSampleRelationship] = useState((_ref3 = (_draft$isSample = draft === null || draft === void 0 ? void 0 : draft.isSample()) !== null && _draft$isSample !== void 0 ? _draft$isSample : locationState === null || locationState === void 0 ? void 0 : locationState.defaultToSampleContact) !== null && _ref3 !== void 0 ? _ref3 : false);
  const [effectiveDate, setEffectiveDate] = useState(() => (draft === null || draft === void 0 ? void 0 : draft.effectiveDateAsDate()) || null);
  const [showCustomerDetailsWarning, setShowCustomerDetailsWarning] = useState(false);
  const [draftId, setDraftId] = useState(draft === null || draft === void 0 ? void 0 : draft.id());
  const getDraftId = useStateInRef(draftId);
  const [reviewers, setReviewers] = useState(draft ? draft.reviewers() : []);
  const [showReviewerErrors, signalShowReviewerErrors] = useSignal();
  const {
    getNow
  } = useIoContext();
  const {
    contactsMap,
    sampleContact,
    fetchContacts
  } = useContactsMap(props.worksAt.id);
  const {
    showSwitchToEditModal
  } = useScheduledProposal({
    draftId: draftId,
    refresh: () => {},
    onCloseCancelSchedulingModal: () => history.push(routes.draftsDashboard)
  });
  const isSavingDraft = useRef(false);
  const [draftStatus, setDraftStatus] = useState(DraftStatus.None);
  const existingContact = clientContactId ? contactsMap[clientContactId] : isSampleRelationship ? sampleContact : undefined;
  const [agreementTitle, setAgreementTitle] = useState((draft === null || draft === void 0 ? void 0 : draft.title()) || defaultAgreementTitle);
  useOnMount(() => {
    if (draft !== null && draft !== void 0 && draft.isScheduled()) {
      showSwitchToEditModal();
    }
  });
  const showAgreementCreationToast = () => overlayContext.showSuccessToast('Agreement was sent successfully');
  const navigationAfterCreation = redirectUrl => {
    const url = redirectUrl || routes.proposalsDashboard;
    if (url === routes.agreementStudio) {
      history.push(url, {
        fromPostAgreement: true
      });
      history.go(0);
    } else {
      history.push(url);
    }
  };
  const {
    getAgreementNotificationLinks
  } = useCopyProposalLink();
  const draftToCommonWithId = draft => {
    const bundles = draft.serviceBundles();
    return {
      id: generateUniqueId('proposal-template'),
      coverLetter: draft.coverLetter(),
      extraClause: draft.extraClause(),
      requirePaymentMethod: draft.requirePaymentMethod(),
      allowManualPayments: draft.allowManualPayments(),
      isExistingCustomer: draft.isExistingCustomer(),
      customNotificationText: draft.customNotificationText(),
      services: draft.services(),
      serviceBundles: bundles.length > 0 ? {
        bundles
      } : undefined,
      netTerms: draft.netTerms(),
      legalTerms: draft.legalTerms(),
      coverCreditCardFees: draft.coverCreditCardFees(),
      /** Will automatic ongoing services be billed at the start or end of the billing-period. */
      amendmentsApprovalMethod: draft.amendmentsApprovalMethod(),
      invoicesApprovalMethod: draft.invoicesApprovalMethod(),
      requestAccountantAccess: draft.requestAccountantAccess(),
      introVideo: draft.introVideo(),
      expirationDate: draft.expirationDate()
    };
  };
  const discardDraft = async () => {
    const loaderName = 'discardDraft';
    overlayContext.startLoader(loaderName);
    try {
      await api.draft.deleteById(draftId);
      history.push(routes.draftsDashboard);
    } finally {
      overlayContext.stopLoader(loaderName);
    }
  };
  const genDraftId = () => {
    if (draftId) {
      return draftId;
    } else {
      const id = generateUniqueId('draft');
      setDraftId(id);
      window.history.replaceState('', 'Draft', routes.draft(id));
      return id;
    }
  };
  const getDraft = data => makeDraft(_objectSpread(_objectSpread({}, data), {}, {
    id: genDraftId(),
    title: agreementTitle,
    clientContactId,
    isSample: isSampleRelationship,
    effectiveDate,
    reviewers,
    metadata: draft === null || draft === void 0 ? void 0 : draft.metadata()
  }));
  const saveDraft = async (data, isMounted) => {
    var _data$legalTerms;
    if (!isMounted()) return;
    isSavingDraft.current = true;
    setDraftStatus(DraftStatus.Loading);
    const invalidLegalTerms = ((_data$legalTerms = data.legalTerms) === null || _data$legalTerms === void 0 ? void 0 : _data$legalTerms.type) === 'pdf' && !data.legalTerms.pdfUniqueName;
    if (invalidLegalTerms) return;
    const draftToSave = getDraft(data);
    const result = await handleRequest(api.draft.save(draftToSave));
    if (!isMounted()) return;
    isSavingDraft.current = false;
    setDraftStatus(result.success ? DraftStatus.Saved : DraftStatus.Error);
  };
  async function submit(data, submitType) {
    var _props$worksAt$key;
    const sendSilently = submitType === SubmitType.SendSilently;
    const externallySign = submitType === SubmitType.ExternallySign;
    const firstName = existingContact ? existingContact.firstName() : '';
    const lastName = existingContact ? existingContact.lastName() : '';
    const companyName = existingContact ? existingContact.companyName() : '';
    const email = existingContact ? existingContact.email() : '';
    reportButtonClick('agreement-studio-topbar-btn-submit');
    logBI(10485);
    if (!newContactValid || !firstName || firstName.length < 2 || !lastName || lastName.length < 2 || !companyName) {
      if (scrollToTopRef.current != null) scrollToTopRef.current();
      setShowCustomerDetailsWarning(true);
      return;
    }
    if (!validateReviewers(reviewers, existingContact)) {
      signalShowReviewerErrors(true);
      return;
    }
    if (!titleValid) {
      setShowAgreementTitleWarning(true);
      return;
    }
    const confirmed = await overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        switch (submitType) {
          case SubmitType.ExternallySign:
            return /*#__PURE__*/_jsx(ConfirmExternallySignModal, {
              hasBilledImmediatelyService: hasBilledImmediatelyService(data.services, effectiveDate, getNow()),
              dismissModal: dismissModal
            });
          case SubmitType.SendSilently:
            return /*#__PURE__*/_jsx(GenerateLinkModal, {
              recipientsCount: reviewers.length + compact([existingContact]).length,
              dismissModal: dismissModal
            });
          case SubmitType.SendProposal:
            return /*#__PURE__*/_jsx(ReadyToSendModal, {
              firstName: firstName,
              lastName: lastName,
              companyName: companyName,
              email: email,
              reviewers: reviewers,
              dismissModal: dismissModal
            });
          case SubmitType.ScheduleProposal:
            return /*#__PURE__*/_jsx(ScheduleProposal, {
              dismissModal: dismissModal,
              draftId: draftId
            });
        }
      }
    });
    if (!confirmed) return;
    if (isSampleRelationship && submitType !== SubmitType.ScheduleProposal) {
      logBI(11901);
      reportToGtm('form-success', 'vendor-sent-test-agreement');
      reportToAppcues('form-success', 'vendor-sent-test-agreement');
      reportToHeap('testAgreement', 'submit', 'vendor-sent-test-agreement');
      reportToHubspot('vendor_sent_a_test_agreement');
    } else if (submitType === SubmitType.SendProposal) {
      logBI(10479, {
        clientEmail: email,
        selectedDay: undefined
      });
      reportProductsFormSubmission('send-agreement-for-approval', [prepareFormObject('buyerName', {
        first: firstName,
        last: lastName
      }), prepareFormObject('buyerEmail', email), prepareFormObject('customerName', companyName), prepareFormObject('serviceCount', data.services.length), prepareFormObject('netTerms', makeNetTerms(data.netTerms)), prepareFormObject('isExistingCustomer', data.isExistingCustomer)]);
    }
    overlayContext.startLoader(createRelationshipLoader);

    // To avoid a race-condition, don't allow to create a relationship while we're saving a draft
    while (isSavingDraft.current) {
      await wait(300);
    }
    if (submitType === SubmitType.ScheduleProposal) {
      overlayContext.stopLoader(createRelationshipLoader);
      navigationAfterCreation(routes.draftsDashboard);
      return;
    }
    const result = await handleRequest(createRelationshipFromDraft(getDraftId(), {
      externallySign,
      sendSilently
    }));
    if (!result.success) {
      const {
        text
      } = result.error.createRelationshipErrorText();
      overlayContext.showErrorToast(text, 'create-relationship-error');
      saveDraft(data, () => true);
      overlayContext.stopLoader(createRelationshipLoader);
      return;
    }
    const key = isSampleRelationship ? 'numSampleAgreements' : 'numSaleAgreements';
    const {
      pending,
      approved
    } = (_props$worksAt$key = props.worksAt[key]) !== null && _props$worksAt$key !== void 0 ? _props$worksAt$key : {
      pending: 0,
      approved: 0
    };
    updateWorksAt(isSampleRelationship, externallySign);
    if (!isSampleRelationship && pending === 0 && approved === 0) {
      // Important note: this event should be sent from the backend, as we can't assure that the "worksAt" is correct.
      // It is a technical gap.
      reportToGtm('form-success', 'user-successfully-sent-first-agreement');
      reportToAppcues('form-success', 'user-successfully-sent-first-agreement');
      reportToHubspot('user_successfully_sent_first_agreement_');
      reportToHeap('agreementStudio', 'submit', 'user-successfully-sent-first-agreement');
    }
    overlayContext.stopLoader(createRelationshipLoader);
    switch (submitType) {
      case SubmitType.ExternallySign:
        overlayContext.showSuccessToast('Agreement was approved successfully', 'externally-sign-success');
        navigationAfterCreation(routes.agreementsDashboard);
        break;
      case SubmitType.SendSilently:
        try {
          const recipients = await getAgreementNotificationLinks(result.data);
          await overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: _ref5 => {
              let {
                dismissModal
              } = _ref5;
              return /*#__PURE__*/_jsx(CopyLinksModal, {
                dismissModal: dismissModal,
                recipients: recipients
              });
            }
          });
          navigationAfterCreation(routes.proposalsDashboard);
        } catch (error) {}
        break;
      case SubmitType.SendProposal:
        try {
          const notification = await getAgreementNotificationLinks(result.data);
          const redirectUrl = await overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: _ref6 => {
              let {
                dismissModal
              } = _ref6;
              return /*#__PURE__*/_jsx(PostAgreementCreation, {
                testId: "agreement-studio-post-creation-modal",
                onClose: dismissModal,
                name: "".concat(firstName, " ").concat(lastName),
                sampleAgreementData: isSampleRelationship ? {
                  email: props.userEmail
                } : undefined,
                proposalLinks: notification
              });
            }
          });
          navigationAfterCreation(redirectUrl);
        } catch (error) {
          sendErrReport('failed to show success modal', error);
          showAgreementCreationToast(); //fail silently in a reasonable way
          navigationAfterCreation();
        }
        break;
    }
  }
  const openPreviewModal = data => {
    reportButtonClick('agreement-previewed-agreement');
    if (!legalTermsDocuments) return;
    const draft = getDraft(data);
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: props => /*#__PURE__*/_jsx(ClientPreview, _objectSpread({
        type: "draft",
        draftInfo: {
          draft,
          sampleContact,
          contactsMap,
          legalTerms: legalTermsDocuments
        }
      }, props))
    });
  };
  async function saveAsTemplate() {
    if (!draftId) return;
    const success = await overlayContext.showModal({
      content: _ref7 => {
        let {
          dismissModal
        } = _ref7;
        return /*#__PURE__*/_jsx(SaveAsTemplateModal, {
          draftId: draftId,
          dismissModal: dismissModal
        });
      }
    });
    if (success) history.push(routes.proposalTemplates);
  }
  return legalTermsDocuments && contactsMap ? /*#__PURE__*/_jsx(AgreementStudioWithLegalTermsModal, _objectSpread(_objectSpread({}, props), {}, {
    draft: draft != null ? draftToCommonWithId(draft) : undefined,
    isSampleRelationship: isSampleRelationship,
    isReadOnly: isViewProposal,
    modalTitle: isViewProposal ? 'View proposal' : 'Create proposal',
    modalTitleTestId: isViewProposal ? 'view-proposal-title' : 'create-proposal-title',
    contact: existingContact,
    legalTermsDocuments: legalTermsDocuments,
    effectiveDate: effectiveDate,
    additionalAutoSaveDependencies: [agreementTitle, clientContactId, effectiveDate, reviewers],
    doScrollToTopRef: scrollToTopRef,
    savingStatusMP: getSavingStatusMP(draftStatus),
    agreementTitleMP: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AgreementName, {
        agreementTitle: agreementTitle,
        setAgreementTitle: setAgreementTitle,
        setAgreementTitleValid: setTitleValid,
        showWarning: showAgreementTitleWarning,
        label: "Agreement name",
        disabled: isViewProposal,
        testid: "agreement-studio-customer-details-input-display-title"
      }), /*#__PURE__*/_jsx(ContactDetails, {
        existingContact: existingContact,
        disabled: isViewProposal,
        refreshExistingContacts: fetchContacts,
        onValidation: setNewContactValid,
        onChange: change => {
          setIsSampleRelationship(change.type === 'sampleContact');
          setClientContactId(change.type === 'existingContact' ? change.id : null);
          if (change.type === 'existingContact') {
            setShowCustomerDetailsWarning(false);
          }
        },
        showCustomerDetailsWarning: showCustomerDetailsWarning
      }), (!!(draft !== null && draft !== void 0 && draft.reviewers().length) || reviewers.length > 0) && /*#__PURE__*/_jsx(GridItem, {
        span: 12,
        children: /*#__PURE__*/_jsx(SignatoryCheckbox, {
          info: 'The primary contact must sign the proposal.',
          checkboxTestid: 'primary-contact-required-sign-checkbox',
          value: true,
          disabled: true,
          infoTestid: 'primary-contact-required-sign-checkbox-info'
        })
      }), /*#__PURE__*/_jsx(AddAdditionalRecipients, {
        primaryContact: existingContact,
        amendmentInfoCallout: existingContact && !isViewProposal && contactHasAgreement(existingContact, agreements, proposals) && /*#__PURE__*/_jsxs(Callout, {
          children: ["It appears you have an agreement with ", existingContact.companyName(), ". To change terms, or add/edit services, edit the existing agreement instead of creating a new one.", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
            openInNewTab: true,
            href: helpArticles.howToEditAgreement(),
            testid: "agreement-studio-amendment-info",
            children: "Learn more"
          })]
        }),
        initialState: reviewers,
        showReviewerErrors: showReviewerErrors,
        disabled: isViewProposal,
        onChange: setReviewers
      }), /*#__PURE__*/_jsx(RelationshipEffectiveDate, {
        value: effectiveDate,
        disabled: isViewProposal,
        onChange: setEffectiveDate,
        title: "Agreement effective date",
        description: "The date when you can start providing the services and bill the client."
      })]
    }),
    inlineFooterActions: isViewProposal ? [_objectSpread(_objectSpread({}, viewProposalMode.sendReminder), {}, {
      testid: 'agreement-studio-send-reminder'
    }), _objectSpread(_objectSpread({}, viewProposalMode.copyLink), {}, {
      testid: 'agreement-studio-copy-link'
    })] : [{
      text: 'Save as template',
      intent: Intent.CreateProposalTemplate,
      testid: 'agreement-studio-save-template',
      disabled: draftId == null,
      onClick: saveAsTemplate,
      tooltipTitle: draftId == null ? 'Make changes to your draft in order to save it as a proposal template' : undefined
    }],
    additionalFooterActions: isViewProposal ? [_objectSpread(_objectSpread({}, viewProposalMode.duplicate), {}, {
      iconStart: /*#__PURE__*/_jsx(CopyLiner24, {}),
      testid: 'agreement-studio-duplicate-proposal'
    }), _objectSpread(_objectSpread({}, viewProposalMode.cancel), {}, {
      iconStart: /*#__PURE__*/_jsx(TrashLine, {}),
      skin: 'error',
      testid: 'agreement-studio-cancel-proposal'
    })] : [{
      text: 'Archive draft',
      disabled: draftId == null || (draft === null || draft === void 0 ? void 0 : draft.scheduledDate()) != null,
      iconStart: /*#__PURE__*/_jsx(ArchiveLiner24, {}),
      testid: AgreementStudioProposalModeTestIds.ArchiveDraft(),
      onClick: () => {
        archive(draftId);
      }
    }, {
      text: 'Discard draft',
      skin: 'error',
      disabled: draftId == null,
      iconStart: /*#__PURE__*/_jsx(TrashLine, {}),
      testid: 'agreement-studio-topbar-discard-draft',
      onClick: discardDraft
    }],
    onSubmitCb: submit,
    onAutoSaveCb: isViewProposal ? undefined : saveDraft,
    openPreviewModal: openPreviewModal,
    renderSubmitButton: _ref8 => {
      let {
        testid,
        className,
        onSubmit
      } = _ref8;
      if (isViewProposal) {
        const {
          text,
          intent,
          disabled,
          onClick
        } = viewProposalMode.edit;
        return /*#__PURE__*/_jsx(Button, {
          intent: intent,
          "data-testid": testid,
          className: className,
          disabled: disabled,
          onClick: onClick,
          children: text
        });
      } else {
        const sendProposalProps = {
          intent: Intent.SendProposal,
          onClick: () => onSubmit(SubmitType.SendProposal)
        };
        return /*#__PURE__*/_jsxs(SplitButton, _objectSpread(_objectSpread({
          label: "Send",
          "data-testid": testid,
          className: className,
          placement: "top",
          modifiers: {
            offset: {
              offset: '0, 5',
              enabled: true
            },
            flip: {
              enabled: false
            }
          }
        }, sendProposalProps), {}, {
          popperClassName: styles.popper,
          children: [/*#__PURE__*/_jsx(MenuItem, _objectSpread(_objectSpread({
            "data-testid": "send-proposal"
          }, sendProposalProps), {}, {
            children: "Send now by email"
          })), /*#__PURE__*/_jsx(MenuItem, {
            "data-testid": "silent-proposal",
            onClick: () => onSubmit(SubmitType.SendSilently),
            children: "Share by link"
          }), /*#__PURE__*/_jsx(MenuItem, {
            "data-testid": "schedule-proposal",
            onClick: () => {
              onSubmit(SubmitType.ScheduleProposal);
            },
            children: "Schedule send"
          }), /*#__PURE__*/_jsx(MenuItem, {
            "data-testid": "sign-externally",
            onClick: () => onSubmit(SubmitType.ExternallySign),
            children: "Approve on behalf of your client"
          })]
        }));
      }
    }
  })) : /*#__PURE__*/_jsx(FunctionalLoader, {
    show: true
  });
}